import { useCallback, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import {
  Building2, Users, ShieldCheck, Crown, QrCode, PhoneCall, DoorOpen,
  RefreshCw, AlertTriangle, Activity as ActivityIcon, Globe2, BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";

import { AdminUsersTab } from "../components/admin/AdminUsersTab";
import { AdminBuildingsTab } from "../components/admin/AdminBuildingsTab";
import { AdminUnitsTab } from "../components/admin/AdminUnitsTab";
import { AdminGuardsTab } from "../components/admin/AdminGuardsTab";
import { AdminPassesTab } from "../components/admin/AdminPassesTab";
import { AdminDoorsTab } from "../components/admin/AdminDoorsTab";
import { AdminActivityTab } from "../components/admin/AdminActivityTab";

function StatCard({ label, value, icon: Icon, testId, accent, loading }) {
  return (
    <div className="card-stroke p-5 flex items-center justify-between" data-testid={testId}>
      <div className="min-w-0">
        <div className="label-tiny">{label}</div>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-16" data-testid={`${testId}-skeleton`} />
        ) : (
          <div className="font-display text-3xl font-mono" data-testid={`${testId}-value`}>
            {value ?? "—"}
          </div>
        )}
      </div>
      <div className={`size-10 rounded-md grid place-items-center shrink-0 ${
        accent ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"
      }`}>
        <Icon size={18} />
      </div>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="card-stroke p-5 flex items-start gap-3 border-destructive/40 bg-destructive/5" data-testid="sa-error">
      <AlertTriangle size={18} className="text-destructive mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Command Center couldn't load some data.</div>
        <div className="text-xs font-mono text-muted-foreground mt-1 break-all">{message}</div>
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} data-testid="sa-error-retry">
        <RefreshCw size={14} className="mr-1.5" /> Retry
      </Button>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      api.get("/stats/platform"),
      api.get("/buildings"),
    ]);
    const [s, b] = results;
    if (s.status === "fulfilled") setStats(s.value.data); else setStats(null);
    if (b.status === "fulfilled") setBuildings(b.value.data); else setBuildings([]);
    const firstFailure = results.find((r) => r.status === "rejected");
    if (firstFailure) {
      const reason = firstFailure.reason;
      const detail = reason?.response?.data?.detail || reason?.message || "Network error";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <header className="mb-8 fade-up flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny flex items-center gap-2">
            <Crown size={12} className="text-accent"/> Super admin · Platform control
          </div>
          <h1 className="font-display text-4xl">FastVi Command Center</h1>
          <p className="text-muted-foreground mt-1">
            Global visibility and control across every building, resident, guard and door on FastVi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/manual">
            <Button size="sm" variant="outline" data-testid="sa-manual">
              <BookOpen size={14} className="mr-1.5"/> Manual
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="sa-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
          <span className="text-xs font-mono px-3 py-1 rounded-full bg-primary text-primary-foreground">
            SUPER ADMIN
          </span>
        </div>
      </header>

      {error && (
        <div className="mb-6 fade-up">
          <ErrorBanner message={error} onRetry={load} />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard label="Users" value={stats?.users_total} icon={Users} testId="sa-stat-users" accent loading={loading} />
        <StatCard label="Buildings" value={stats?.buildings} icon={Building2} testId="sa-stat-buildings" loading={loading} />
        <StatCard label="Units" value={stats?.units} icon={Building2} testId="sa-stat-units" loading={loading} />
        <StatCard label="Guards" value={stats?.guards} icon={ShieldCheck} testId="sa-stat-guards" loading={loading} />
        <StatCard label="Active passes" value={stats?.passes_active} icon={QrCode} testId="sa-stat-passes" loading={loading} />
        <StatCard label="Calls (all-time)" value={stats?.calls_total} icon={PhoneCall} testId="sa-stat-calls" loading={loading} />
      </div>

      <Tabs defaultValue="users" className="fade-up">
        <TabsList data-testid="sa-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="sa-tab-users"><Users size={14} className="mr-1.5"/>Users</TabsTrigger>
          <TabsTrigger value="buildings" data-testid="sa-tab-buildings"><Building2 size={14} className="mr-1.5"/>Buildings</TabsTrigger>
          <TabsTrigger value="units" data-testid="sa-tab-units"><Building2 size={14} className="mr-1.5"/>Units</TabsTrigger>
          <TabsTrigger value="guards" data-testid="sa-tab-guards"><ShieldCheck size={14} className="mr-1.5"/>Guards</TabsTrigger>
          <TabsTrigger value="passes" data-testid="sa-tab-passes"><QrCode size={14} className="mr-1.5"/>Passes</TabsTrigger>
          <TabsTrigger value="doors" data-testid="sa-tab-doors"><DoorOpen size={14} className="mr-1.5"/>Doors</TabsTrigger>
          <TabsTrigger value="activity" data-testid="sa-tab-activity"><Globe2 size={14} className="mr-1.5"/>Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <AdminUsersTab buildings={buildings} />
        </TabsContent>
        <TabsContent value="buildings" className="mt-6">
          <AdminBuildingsTab onChanged={load} />
        </TabsContent>
        <TabsContent value="units" className="mt-6">
          <AdminUnitsTab buildings={buildings} />
        </TabsContent>
        <TabsContent value="guards" className="mt-6">
          <AdminGuardsTab buildings={buildings} />
        </TabsContent>
        <TabsContent value="passes" className="mt-6">
          <AdminPassesTab buildings={buildings} />
        </TabsContent>
        <TabsContent value="doors" className="mt-6">
          <AdminDoorsTab buildings={buildings} />
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          <AdminActivityTab buildings={buildings} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
